import React, { useEffect, useState } from "react";
import "../../styles/GxConfirmPasswordModal.scss";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { useT } from "@/app/hooks/useT";
import { Constants } from "@/app/utils/AppConstants";

interface EyeIconProps {
  visible: boolean;
}

const LockIcon = () => <img src={`${ICON_BASE_PATH}unlock.svg`} alt="" />;

const EyeIcon = ({ visible }: EyeIconProps) => {
  return !visible ? (
    <img src={`${ICON_BASE_PATH}eyeopen.svg`} alt="open" />
  ) : (
    <img src={`${ICON_BASE_PATH}eyeclose.svg`} alt="close" />
  );
};

export interface GxConfirmPasswordModalProps {
  open: boolean;
  onClose?: () => void;
  onConfirm?: (password: string) => void;
}

export default function GxConfirmPasswordModal({
  open,
  onClose = () => {},
  onConfirm = () => {},
}: Readonly<GxConfirmPasswordModalProps>) {
  const t = useT();
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const resetAndClose = () => {
    onClose();
    setPassword("");
    setError("");
  };

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") resetAndClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const validationError = validateConfirmPassword(password);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    onConfirm(password);
    resetAndClose();
  };

  const validateConfirmPassword = (password: string): string => {
    if (!password.trim()) {
      return "Password is required.";
    }

    if (password.length > 128) {
      return "Password is too long.";
    }

    if (password.length < 8) {
      return "Password is too short.";
    }

    if (password !== Constants.dummyPassword) {
      return "The password you entered is incorrect";
    }
    return "";
  };

  return (
    <div className="gx-password-modal-overlay">
      <div className="gx-password-modal">
        <div className="gx-password-modal-close">
          <button className="gx-dialog-close-button" onClick={resetAndClose}>
            <img
              className="gx-icon-primary"
              src={`${ICON_BASE_PATH}close.svg`}
              alt="Close"
            />
          </button>
        </div>

        <p className="gx-password-modal-eyebrow gx-p14-sb">
          {t("confirm_password")}
        </p>
        <h2
          id="gx-password-modal-heading"
          className="gx-password-modal-heading gx-p20-b"
        >
          {t("verify_its_you")}
        </h2>
        <p className="gx-password-modal-subtext gx-p13-r">
          {t("enter_your_password_to_confirm_and_authorize")}
        </p>

        <form className="gx-password-modal-form" onSubmit={handleSubmit}>
          <div
            className={`gx-password-modal-field ${error ? "gx-password-modal-field-error" : ""}`}
          >
            <span className="gx-password-modal-icon">
              <LockIcon />
            </span>
            <input
              id="gx-password-modal-password"
              className="gx-password-modal-input"
              type={showPassword ? "text" : "password"}
              placeholder=" "
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value;
                setPassword(value);

                if (error) {
                  setError(validateConfirmPassword(value));
                }
              }}
              autoFocus
            />
            <label
              htmlFor="gx-password-modal-password"
              className="gx-password-modal-floating-label gx-p16-sb"
            >
              {t("password")}
            </label>
            <button
              type="button"
              className="gx-password-modal-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              <EyeIcon visible={!showPassword} />
            </button>
          </div>
          {error && <p className="gx-password-modal-error-text">{error}</p>}

          <div className="gx-password-modal-actions">
            <button
              type="button"
              className="gx-password-modal-cancel gx-p16-b"
              onClick={resetAndClose}
            >
              {t("Cancel")}
            </button>
            <button type="submit" className="gx-password-modal-submit gx-p16-b">
              {t("Confirm")}
            </button>
          </div>
        </form>

        {/* <iframe
          src="https://sitlogin.growx.global/growx/api/v1/idp/auth/login?clientId=GROWX003&redirectUri=https%3A%2F%2Fsit.growx.global%2F&codeChallenge=XhGofdvPoekQy0i-JFjSt9MMiZEBbpY8MjNDOSRTd-E&codeChallengeMethod=S256"
          title="IDP Login"
          width="100%"
          height="100%"
        /> */}
      </div>
    </div>
  );
}
